// Which organisation the signed-in admin is looking at — resolved once, shared
// by every admin screen.
//
// WHY THIS EXISTS. Before it, `AdminAuthGate` authenticated but never
// authorized: any signed-in adult who reached /admin got the full admin shell
// rendered over fixture data for an organisation ("Bright Steps Centre") they
// have nothing to do with. Separately, each live screen re-derived the org id
// itself with `memberships.find(m => m.accountId === userId && m.role ===
// "admin") ?? memberships[0]`, silently falling back to *any* membership and
// then to the empty string — so a member who is not an admin issued writes that
// Postgres rejected with a raw 42501, and a person with no membership at all
// issued writes against orgId "".
//
// Resolving it in one place, once, means: the gate can refuse before rendering,
// every screen reads the same id, and the "you have no organisation" and "you
// are not an admin" states become real screens instead of undefined behaviour.
//
// It also runs the invitation hand-off. `invite_member` records a row and sends
// no email (there is no Edge Function); `accept_member_invitations()` is what
// turns that row into a membership. Calling it here — once per sign-in, before
// resolving the org — is what makes "admin invites you, you sign in, you're in"
// work end to end.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { getPortalData, shouldUseSupabase } from "../../data";
import type { MembershipRole } from "../../domain/types";

export type OrgStatus =
  | "loading"
  /** Signed in, but belongs to no organisation — offer to create one. */
  | "none"
  /** Belongs to an organisation as a plain member — the admin portal is not for them. */
  | "not_admin"
  /** Belongs to an organisation as an admin. */
  | "admin"
  /** The lookup itself failed (network / RLS / server error). */
  | "error";

export interface OrgState {
  status: OrgStatus;
  orgId: string;
  orgName: string;
  orgType: string;
  role: MembershipRole | null;
  /** Present only when status === "error". */
  error: string;
  /** How many pending invitations were accepted during this sign-in. */
  acceptedInvites: number;
  /** Re-runs the whole resolution (after creating an org, or after a failure). */
  refresh: () => void;
}

const DEMO_ORG: OrgState = {
  status: "admin",
  orgId: "org-brightsteps",
  orgName: "Bright Steps Centre",
  orgType: "centre",
  role: "admin",
  error: "",
  acceptedInvites: 0,
  refresh: () => {},
};

const OrgCtx = createContext<OrgState>(DEMO_ORG);

/** The resolved organisation for the current admin session. */
export function useOrg(): OrgState {
  return useContext(OrgCtx);
}

/**
 * Resolves the caller's organisation.
 *
 * `accountId` is the signed-in Supabase user id. It is required to tell "admin
 * of this org" from "member of this org": `memberships()` returns every member
 * of the caller's org (RLS `membership_select_org` scopes to the org, not to the
 * row), so picking the caller's own row by id is the only correct read.
 */
export function OrgProvider({
  accountId,
  children,
}: {
  accountId?: string;
  children: ReactNode;
}) {
  const [state, setState] = useState<Omit<OrgState, "refresh">>({
    status: "loading",
    orgId: "",
    orgName: "",
    orgType: "",
    role: null,
    error: "",
    acceptedInvites: 0,
  });
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!shouldUseSupabase()) return;

    let cancelled = false;
    setState((s) => ({ ...s, status: "loading", error: "" }));

    (async () => {
      const data = getPortalData();
      let accepted = 0;

      // Step 1: consume any invitation addressed to this email. A failure here
      // must NOT block sign-in — someone who is already a member has nothing to
      // accept, and a transient failure should still let them into their org.
      try {
        accepted = await data.acceptMemberInvitations();
      } catch {
        accepted = 0;
      }

      // Step 2: resolve the membership. Reading memberships BEFORE organisations
      // means one wasted round-trip less for the (common) no-org case.
      try {
        const memberships = await data.memberships();
        if (cancelled) return;

        const mine = accountId
          ? memberships.filter((m) => m.accountId === accountId)
          : memberships;

        if (mine.length === 0) {
          setState({
            status: "none",
            orgId: "",
            orgName: "",
            orgType: "",
            role: null,
            error: "",
            acceptedInvites: accepted,
          });
          return;
        }

        // Prefer an admin membership when someone holds several — that is the
        // one the admin portal is for. Deterministic (sorted) so a reload never
        // silently switches which org is on screen.
        const sorted = [...mine].sort((a, b) =>
          a.role === b.role
            ? a.orgId.localeCompare(b.orgId)
            : a.role === "admin"
              ? -1
              : 1,
        );
        const chosen = sorted[0];

        const orgs = await data.organisations();
        if (cancelled) return;
        const org = orgs.find((o) => o.id === chosen.orgId);

        setState({
          status: chosen.role === "admin" ? "admin" : "not_admin",
          orgId: chosen.orgId,
          orgName: org?.name ?? "",
          orgType: org?.type ?? "",
          role: chosen.role,
          error: "",
          acceptedInvites: accepted,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          orgId: "",
          orgName: "",
          orgType: "",
          role: null,
          error:
            err instanceof Error
              ? err.message
              : "We could not load your organisation.",
          acceptedInvites: accepted,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, nonce]);

  // With no Supabase configured (local dev, the whole test suite) the admin
  // portal runs on fixtures, so hand every screen the fixture org rather than a
  // perpetual loading state.
  const value: OrgState = shouldUseSupabase()
    ? { ...state, refresh }
    : { ...DEMO_ORG, refresh };

  return <OrgCtx.Provider value={value}>{children}</OrgCtx.Provider>;
}

/** Test seam: renders children with an arbitrary org state. */
export function OrgProviderForTest({
  value,
  children,
}: {
  value: Partial<OrgState>;
  children: ReactNode;
}) {
  return (
    <OrgCtx.Provider value={{ ...DEMO_ORG, ...value }}>
      {children}
    </OrgCtx.Provider>
  );
}
